// src/components/home/TechStackSection.js
import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCoverflow, Pagination, Navigation, Autoplay } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import 'swiper/css/autoplay';

import './TechStackSection.css';

// Données de la stack technologique (vous devriez les mettre dans votre fichier techStackData.js)
const techStackData = [
  { name: 'React', imageUrl: '/images/logos/react1.png', altText: 'Logo React', category: 'Frontend Framework', utility: 'Bibliothèque JavaScript pour construire des interfaces utilisateur interactives et des applications monopages performantes.' },
  { name: 'Android', imageUrl: '/images/logos/android1.png', altText: 'Logo Android', category: 'Mobile OS', utility: 'Système d\'exploitation mobile pour développer des applications natives riches sur smartphones et tablettes.' },
  { name: 'Figma', imageUrl: '/images/logos/figma1.png', altText: 'Logo Figma', category: 'Design Tool', utility: 'Outil de design collaboratif pour créer des interfaces utilisateur, prototypes interactifs et systèmes de design.' },
  { name: 'Apple iOS', imageUrl: '/images/logos/apple1.png', altText: 'Logo Apple iOS', category: 'Mobile Ecosystem', utility: 'Développement d\'applications natives pour iPhone et iPad, offrant une expérience utilisateur premium et sécurisée.' },
  { name: 'Supabase', imageUrl: '/images/logos/supabase1.png', altText: 'Logo Supabase', category: 'Backend as a Service', utility: 'Alternative open-source à Firebase, offrant base de données, authentification, et APIs temps réel.' },
  { name: 'WordPress', imageUrl: '/images/logos/wordpress1.png', altText: 'Logo WordPress', category: 'CMS', utility: 'Système de gestion de contenu leader pour créer des sites web, blogs et plateformes e-commerce flexibles.' },
  { name: 'TypeScript', imageUrl: '/images/logos/ts.png', altText: 'Logo TypeScript', category: 'Langage', utility: 'Sur-ensemble de JavaScript ajoutant un typage statique pour des applications plus robustes et maintenables.' },
  { name: 'UI Design', imageUrl: '/images/logos/ui.png', altText: 'Icône UI Design', category: 'Design', utility: 'Conception d\'interfaces utilisateur axée sur l\'esthétique et l\'interaction pour une expérience engageante.' },
  { name: 'Vue.js', imageUrl: '/images/logos/vu.png', altText: 'Logo Vue.js', category: 'Frontend Framework', utility: 'Framework JavaScript progressif pour construire des interfaces utilisateur et des applications monopages réactives.' },
  { name: 'Svelte', imageUrl: '/images/logos/svelte.png', altText: 'Logo Svelte', category: 'Compilateur UI', utility: 'Compilateur qui transforme vos composants en code JavaScript impératif optimisé pour une vitesse maximale.' },
  { name: 'Linux', imageUrl: '/images/logos/linux1.png', altText: 'Logo Linux', category: 'Système d\'exploitation', utility: 'OS open-source réputé pour sa stabilité et flexibilité, idéal pour les serveurs et environnements de développement.' },
  { name: 'CSS3', imageUrl: '/images/logos/css.png', altText: 'Logo CSS3', category: 'Styling Language', utility: 'Langage de feuilles de style pour décrire la présentation et le design des documents web modernes.' },
  { name: 'Flutter', imageUrl: '/images/logos/flutter1.png', altText: 'Logo Flutter', category: 'UI Toolkit', utility: 'Kit de Google pour créer des applications compilées nativement pour mobile, web et bureau depuis une base de code unique.' },
  { name: 'Firebase', imageUrl: '/images/logos/firebase1.png', altText: 'Logo Firebase', category: 'Backend Platform', utility: 'Plateforme Google offrant base de données NoSQL, authentification, hébergement et services backend.' },
  { name: 'GitHub', imageUrl: '/images/logos/github.png', altText: 'Logo GitHub', category: 'Version Control Platform', utility: 'Plateforme de développement collaboratif et d\'hébergement de code utilisant Git pour le contrôle de version.' },
  { name: 'Google', imageUrl: '/images/logos/google.png', altText: 'Logo Google', category: 'Technologies & Services', utility: 'Vaste écosystème d\'outils pour le développement, l\'analyse, la publicité et l\'infrastructure cloud.' },
  { name: 'HTML5', imageUrl: '/images/logos/html.png', altText: 'Logo HTML5', category: 'Markup Language', utility: 'Langage de balisage standard pour structurer le contenu des pages web et des applications.' },
  { name: 'JavaScript', imageUrl: '/images/logos/js.png', altText: 'Logo JavaScript', category: 'Langage de programmation', utility: 'Langage essentiel du web pour des interactions dynamiques, côté client comme serveur (Node.js).' },
  { name: 'Unity', imageUrl: '/images/logos/unity.png', altText: 'Logo Unity', category: 'Game Engine', utility: 'Moteur de jeu multiplateforme pour créer des jeux 2D/3D, simulations et expériences interactives riches.' },
  { name: 'Vite', imageUrl: '/images/logos/vite.png', altText: 'Logo Vite', category: 'Build Tool', utility: 'Outil de build frontend moderne offrant un serveur de développement ultra-rapide et une compilation optimisée.' }
];

function TechStackSection() {
  return (
    <section className="tech-stack-container">
      <h2 className="section-title tss-title">Notre Stack Technologique Full-Stack</h2>
      <p className="section-intro">
        Nous maîtrisons un large éventail de technologies modernes pour construire des applications robustes et évolutives, du frontend au backend.
      </p>
      <Swiper
        modules={[EffectCoverflow, Pagination, Navigation, Autoplay]}
        effect={'coverflow'}
        grabCursor={true}
        centeredSlides={true}
        loop={true}
        slidesPerView={'auto'}
        coverflowEffect={{
          rotate: 30,
          stretch: 0,
          depth: 100,
          modifier: 1,
          slideShadows: true,
        }}
        pagination={{
          clickable: true,
          dynamicBullets: true,
        }}
        navigation={true}
        autoplay={{
          delay: 3000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        className="tech-swiper"
        breakpoints={{
          320: { slidesPerView: 1, spaceBetween: 20, coverflowEffect: { rotate: 50, stretch: 0, depth: 100, modifier: 1 }},
          640: { slidesPerView: 2, spaceBetween: 20 },
          768: { slidesPerView: 3, spaceBetween: 30 },
          1024: { slidesPerView: 4, spaceBetween: 40 } // 4 slides sur desktop pour voir plus de logos
        }}
      >
        {techStackData.map(tech => (
          <SwiperSlide key={tech.name} className="tech-swiper-slide">
            <div className="tech-card">
              <img src={tech.imageUrl} alt={tech.altText || tech.name} className="tech-logo-image" />
              <h3>{tech.name}</h3>
              <p className="tech-utility">{tech.utility}</p>
              <p className="tech-category">{tech.category}</p>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  );
}

export default TechStackSection;